import { useQuery } from '@tanstack/react-query'

import {
  AttachmentsByStage,
  DashboardRequestDetails,
  Department,
  RequestActivityLogs,
  RequestLifecycleStage,
  RequestStage,
  UserPermissions,
} from '~shared/types'
import { Request } from '~shared/types'

import {
  getAttachmentsByStages,
  getCurrentRequestStage,
  getDepartments,
  getRequest,
  getRequestActivityLogs,
  getRequestLifecycle,
  getRequests,
  getRequestTypes,
  getUserPermissionsForRequest,
} from '~/api/request'

export const requestKeys = {
  departments: ['departments'] as const,
  requestTypes: ['requestTypes'] as const,
  all: ['requests'] as const,
  detail: (id: string) => [...requestKeys.all, id] as const,
  lifecycle: (id: string) => [...requestKeys.detail(id), 'stages'] as const,
  attachments: (id: string) =>
    [...requestKeys.detail(id), 'attachments'] as const,
  currentStage: (id: string) =>
    [...requestKeys.detail(id), 'currentStage'] as const,
  userPermissions: (id: string) =>
    [...requestKeys.detail(id), 'user-permissions'] as const,
  activityLogs: (id: string) =>
    [...requestKeys.detail(id), 'activity-logs'] as const,
}

export const useGetDepartmentsQuery = () => {
  return useQuery({
    queryKey: requestKeys.departments,
    queryFn: async () => {
      const response = await getDepartments()
      const data = response.data as Department[]
      return data
    },
  })
}

export const useGetRequestTypesQuery = () => {
  return useQuery({
    queryKey: requestKeys.requestTypes,
    queryFn: async () => {
      const response = await getRequestTypes()
      const data = response.data as string[]
      return data
    },
  })
}

export const useGetAllRequestsQuery = () => {
  return useQuery({
    queryKey: requestKeys.all,
    queryFn: async () => {
      const response = await getRequests()
      const data = response.data as DashboardRequestDetails[]
      return data
    },
  })
}

export const useGetRequestQuery = (id: string) => {
  return useQuery({
    queryKey: requestKeys.detail(id),
    queryFn: async () => {
      const response = await getRequest(id)
      if (response.status === 404) {
        return null
      }
      return response.data as Request
    },
  })
}

export const useGetRequestLifecycleQuery = (id: string) => {
  return useQuery({
    queryKey: requestKeys.lifecycle(id),
    queryFn: async () => {
      const response = await getRequestLifecycle(id)
      return response.data as RequestLifecycleStage[]
    },
  })
}

export const useGetRequestCurrentStageQuery = (id: string) => {
  return useQuery({
    queryKey: requestKeys.currentStage(id),
    queryFn: async () => {
      const response = await getCurrentRequestStage(id)
      return response.data as RequestStage
    },
  })
}

export const useGetRequestUserPermissionsQuery = (id: string) => {
  return useQuery({
    queryKey: requestKeys.userPermissions(id),
    queryFn: async () => {
      const response = await getUserPermissionsForRequest(id)
      return response.data as UserPermissions
    },
  })
}

export const useGetRequestActivityLogsQuery = (id: string) => {
  return useQuery({
    queryKey: requestKeys.activityLogs(id),
    queryFn: async () => {
      const response = await getRequestActivityLogs(id)
      return response.data as RequestActivityLogs[]
    },
  })
}

export const useGetAttachmentsByStagesQuery = (id: string) => {
  return useQuery({
    queryKey: requestKeys.attachments(id),
    queryFn: async () => {
      const response = await getAttachmentsByStages(id)
      return response.data as AttachmentsByStage[]
    },
  })
}
